import { useMemo } from "react";

import { useTheme, type SxProps, type Theme } from "@mui/material/styles";

import { dirtyInputDark } from "@waterly/utils-isomorphic";

import { InputMode } from "./DataEntryInput.types";

const primaryText = "text.primary";
export const dirtyBorder = `2px solid ${dirtyInputDark}`;

const calculateUniversalStyles = () => {
  return {
    backgroundColor: "background.paper",
    color: primaryText,
  };
};

const calculateCalculatedStyles = () => {
  return {
    backgroundColor: "action.hover",
  };
};

const calculateDirtyStyles = ({ isYesNoInput }: { isYesNoInput: boolean }) => {
  return {
    backgroundColor: isYesNoInput ? undefined : "inputBackgroundDirty.main",
    // for text field select inputs
    "& .MuiOutlinedInput-root": { backgroundColor: "inputBackgroundDirty.main" },
    "& .MuiOutlinedInput-notchedOutline": { border: dirtyBorder },
  };
};

const calculateReadonlySiteEdit = () => {
  return {
    "&.Mui-disabled": { backgroundColor: "action.disabledBackground" },
    // for text field select inputs
    "& .MuiOutlinedInput-root": { backgroundColor: "action.disabledBackground" },
  };
};

const calculateReadOnlyStyles = ({ theme }: { theme: Theme }) => {
  return {
    "&.Mui-disabled > input": { color: primaryText, WebkitTextFillColor: theme.palette.text.primary },
    "&.Mui-disabled textarea": { color: primaryText, WebkitTextFillColor: theme.palette.text.primary },
  };
};

export type UseInputSxOptions = {
  mode: InputMode;
  isDirty: boolean;
  isCalculated?: boolean;
  isYesNoInput?: boolean;
};

export const useInputSx = ({
  mode,
  isDirty,
  isCalculated = false,
  isYesNoInput = false,
}: UseInputSxOptions): SxProps => {
  const theme = useTheme();
  const isReadOnly = mode === InputMode.ReadOnly;
  const isSiteEdit = mode === InputMode.SiteEdit;

  return useMemo(
    () => [
      calculateUniversalStyles(),
      isCalculated && calculateCalculatedStyles(),
      isDirty && calculateDirtyStyles({ isYesNoInput }),
      isReadOnly && calculateReadOnlyStyles({ theme }),
      (isReadOnly || isSiteEdit) && calculateReadonlySiteEdit(),
    ],
    [isCalculated, isDirty, isReadOnly, isSiteEdit, isYesNoInput, theme],
  );
};
